import React, { useEffect, useState } from 'react';
import { Modal } from '@ofself/shell';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import api from '../../services/api';
import { autosApi, pluginsApi } from './data/api';
import { fetchPlugins } from './data/items';
import { triggerKindWord, frequencyWord } from './data/words';
import {
    EVENTS, eventWord, automationError, schemaLabel, schemaWord, chainPlan, chainBody, cannotStandAt, startsPhrase, isToolSchedule, standingOf,
} from './data/lists';
import { listWords } from './data/words';
import Picker from '../../components/nucleus/Picker';
import StepSheet from '../../components/nucleus/StepSheet';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import './PluginsSection.css';

// THE AUTOMATIONS PAGE'S SHEETS:
//   AutomationSheet — a new automation, or one edited: what starts it (a
//                     change, or when you run it) and which plugin's tool it
//                     runs, in which realm. A tool runs automatically from its
//                     own sheet: "Runs automatically" opens that tool's tab instead.
//   FlowSheet       — a flow, made in steps: its name, then its automations in
//                     the order they run (a guided making sheet, StepSheet)
//   ReadSheet       — raw details, read only, where nothing reads them better
// A plugin's own sheets (its setup, a tool's Run and Runs automatically) are its
// inspector's: PluginSetup.js, ToolSheet.js.

const TRIGGERS = ['schedule', 'event', 'manual'].map((id) => ({ value: id, label: triggerKindWord(id) }));
const TRIGGER_KEYS = ['cron', 'event', 'schema_id', 'tool_id'];
const capital = (w) => (w ? `${w[0].toUpperCase()}${w.slice(1)}` : w);
// a node event names its schema; a file has none
const takesSchema = (event) => String(event || '').startsWith('node.');

/** What the sheet will make, in one line: "When a note is created, run Mailroom · Send email". */
export function previewOf({ trigger, cron, event, schemaName = null, pluginName, toolName = null }) {
    const what = `run ${[pluginName || '…', toolName].filter(Boolean).join(' · ')}`;
    if (trigger === 'schedule') return `${cron ? frequencyWord(cron) : triggerKindWord('schedule')}, ${what}`;
    if (trigger === 'event') {
        const e = eventWord(event, { schemaName });
        return `When ${e[0].toLowerCase()}${e.slice(1)}, ${what}`;
    }
    return `${triggerKindWord('manual')}: ${what}`;
}

/** A labelled field in a sheet: its visible name over its input. */
function Field({ id, label, children }) {
    return (
        <div className="plugins-sheetfield">
            <label className="plugins-label" htmlFor={id}>{label}</label>
            {children}
        </div>
    );
}

/**
 * POST a new automation, or PUT an edited one, in the space. It runs one tool
 * of an installed plugin (a plugin with several always names it), under one of
 * the space's realms that let it run. `onSaved` hears `{ pluginId, toolId }`;
 * `onSchedule({ pluginId, toolId })` opens a tool's Schedule tab, where its
 * one schedule is made. `plugins`, when given, are the installed plugins.
 */
export function AutomationSheet({ groupId, automation = null, plugins: given = null, onClose, onSaved, onSchedule }) {
    const editing = !!automation;
    const tc = automation?.triggerConfig || {};
    const [name, setName] = useState(automation?.name || '');
    const [detail, setDetail] = useState(automation?.blurb || '');
    const [plugin, setPlugin] = useState(automation?.pluginId || '');
    const [trigger, setTrigger] = useState(TRIGGERS.some((t) => t.value === automation?.trigger) ? automation.trigger : 'event');
    const [event, setEvent] = useState(tc.event && EVENTS.some((e) => e.value === tc.event) ? tc.event : 'node.created');
    const [schema, setSchema] = useState(tc.schema_id || '');
    const [tool, setTool] = useState(tc.tool_id || '');
    const [realm, setRealm] = useState('');
    const [loaded, setLoaded] = useState(given);
    const [setup, setSetup] = useState(null);
    const [schemas, setSchemas] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let live = true;
        if (!given) fetchPlugins(groupId).then((p) => { if (live) setLoaded(p.filter((x) => x.installed)); }).catch((e) => { if (live) { setLoaded([]); setError(automationError(e, 'Couldn’t load your plugins.')); } });
        Promise.resolve(api.getSchemas?.({ limit: 200, ...(groupId ? { group_id: groupId } : {}) })).then((s) => { if (live) setSchemas(s || []); }).catch(() => {});
        return () => { live = false; };
    }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps
    const plugins = (given || loaded || []).filter((p) => p.installed);

    const chosen = plugins.find((p) => String(p.id) === String(plugin)) || null;
    const isPlugin = !!chosen;

    // THE CHOSEN PLUGIN'S REALMS: the space's own that let it run, and the one
    // an automation being edited runs under now (kept unless another is chosen)
    useEffect(() => {
        if (!isPlugin) { setSetup(null); return undefined; }
        let live = true;
        pluginsApi.setupConfig(plugin, groupId).then((s) => {
            if (!live) return;
            setSetup(s || {});
            const now = automation?.pluginConfig?.privacy_realm_id
                || (s?.automations || []).find((a) => String(a.id) === String(automation?.id))?.privacy_realm_id;
            const realms = s?.eligible_realms || [];
            const preferred = realms.find((r) => r.is_default) || realms[0];
            setRealm((cur) => cur || now || (editing ? '' : preferred?.id || ''));
        }).catch(() => { if (live) setSetup({}); });
        return () => { live = false; };
    }, [isPlugin, plugin, groupId, automation?.id, editing]); // eslint-disable-line react-hooks/exhaustive-deps

    // WHICH TOOL IT RUNS: asked whenever the plugin has more than one
    const tools = (chosen?.tools || []).filter((t) => t.tool_id).map((t) => ({ value: t.tool_id, label: t.name || t.tool_id }));
    useEffect(() => { if (tools.length === 1 && tool !== tools[0].value) setTool(tools[0].value); }, [tools.length, chosen?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const choosePlugin = (id) => { setPlugin(id); setTool(''); setRealm(''); setError(null); };
    const realms = setup?.eligible_realms || [];
    const before = automation?.pluginConfig?.privacy_realm_id
        || (setup?.automations || []).find((a) => String(a.id) === String(automation?.id))?.privacy_realm_id || null;
    const toolName = tools.find((t) => t.value === tool)?.label || null;
    const schemaName = schemaWord(schemas.find((s) => String(s.id) === String(schema)));
    const pluginName = chosen?.name || automation?.plugin;

    const save = async () => {
        // keep whatever else the trigger carries (its zone, its allowed callers, the step before it)
        const config = Object.fromEntries(Object.entries(tc).filter(([k]) => !TRIGGER_KEYS.includes(k)));
        if (trigger === 'event') { config.event = event; if (schema && takesSchema(event)) config.schema_id = schema; }
        if (tool) config.tool_id = tool;
        setBusy(true);
        setError(null);
        try {
            if (editing) {
                const body = { name, description: detail || null, trigger_type: trigger, trigger_config: config };
                if (isPlugin && realm && realm !== before) body.plugin_config = { ...(automation.pluginConfig || {}), privacy_realm_id: realm };
                await autosApi.update(groupId, automation.id, body);
            } else {
                const body = { name, plugin_id: plugin, trigger_type: trigger, trigger_config: config, plugin_config: { remove_source: false, privacy_realm_id: realm } };
                if (detail) body.description = detail;
                await autosApi.create(groupId, body);
            }
            onSaved?.({ pluginId: plugin || null, toolId: tool || '' });
        } catch (e) {
            setError(automationError(e, `It could not be ${editing ? 'saved' : 'made'}.`));
        } finally {
            setBusy(false);
        }
    };

    const scheduling = trigger === 'schedule';
    const toolPicked = !tools.length || !!tool;
    const ready = !!name.trim() && (editing || !!plugin) && toolPicked && (!isPlugin || editing || !!realm);
    const realmOptions = realms.map((r) => ({ value: r.id, label: r.is_default ? `${r.name} (default)` : r.name }));
    return (
        <Modal open onClose={onClose} title={editing ? `Edit ${automation.name}` : 'New automation'} size="md">
            <div className="plugins-form">
                <Field id="plugins-sheet-plugin" label="Plugin">
                    {editing
                        ? <input id="plugins-sheet-plugin" className="plugins-input" readOnly value={automation.plugin || 'No plugin'} />
                        : <Picker id="plugins-sheet-plugin" fill label="Plugin" placeholder="Choose a plugin" value={plugin} onChange={choosePlugin}
                            options={plugins.map((p) => ({ value: p.id, label: p.name }))} />}
                </Field>
                {tools.length > 1 && (
                    <Field id="plugins-sheet-tool" label="Tool">
                        <Picker id="plugins-sheet-tool" fill label="Tool" placeholder="Choose a tool" value={tool} onChange={setTool} options={tools} />
                    </Field>
                )}
                {tools.length === 1 && (
                    <Field id="plugins-sheet-tool" label="Tool"><input id="plugins-sheet-tool" className="plugins-input" readOnly value={tools[0].label} /></Field>
                )}

                <Field id="plugins-sheet-trigger" label="When it runs">
                    <Picker id="plugins-sheet-trigger" fill label="When it runs" options={TRIGGERS} value={trigger} onChange={(v) => { setTrigger(v); setError(null); }} />
                </Field>
                {scheduling ? (
                    <p className="plugins-sheetnote">{toolName || tools.length <= 1
                        ? `A tool is set to run automatically on the tool itself, in ${pluginName || 'its plugin'}.`
                        : 'A tool is set to run automatically on the tool itself. Choose the tool.'}</p>
                ) : (
                    <>
                        <Field id="plugins-sheet-name" label="Name">
                            <input id="plugins-sheet-name" className="plugins-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                        </Field>
                        <Field id="plugins-sheet-detail" label="What it does (optional)">
                            <input id="plugins-sheet-detail" className="plugins-input" value={detail} onChange={(e) => setDetail(e.target.value)} />
                        </Field>
                        {isPlugin && (
                            <Field id="plugins-sheet-realm" label="Runs in">
                                <Picker id="plugins-sheet-realm" fill label="Runs in" value={realm} onChange={setRealm}
                                    placeholder={setup && !realms.length ? 'No realm here lets it run' : 'Choose a realm'}
                                    options={realmOptions} />
                            </Field>
                        )}
                        {trigger === 'event' && (
                            <div className="plugins-sheetpair">
                                <Field id="plugins-sheet-event" label="What changes">
                                    <Picker id="plugins-sheet-event" fill label="What changes" value={event} onChange={setEvent} options={EVENTS.map((e) => ({ value: e.value, label: e.label }))} />
                                </Field>
                                {takesSchema(event) && (
                                    <Field id="plugins-sheet-schema" label="Schema">
                                        <Picker id="plugins-sheet-schema" fill label="Schema" value={schema} onChange={setSchema}
                                            options={[{ value: '', label: 'Any schema' }, ...schemas.filter((s) => schemaLabel(s)).map((s) => ({ value: s.id, label: schemaLabel(s) }))]} />
                                    </Field>
                                )}
                            </div>
                        )}
                        {(plugin || editing) && <p className="plugins-preview" aria-live="polite">{previewOf({ trigger, event, schemaName, pluginName, toolName })}</p>}
                    </>
                )}
                {error && <p className="plugins-error" role="alert">{error}</p>}
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    {scheduling
                        ? (
                            <FootButton primary disabled={!plugin || !toolPicked} onClick={() => onSchedule?.({ pluginId: plugin, toolId: tool || '' })}>
                                {toolName ? `Open ${toolName}` : 'Open the tool'}
                            </FootButton>
                        )
                        : <FootButton primary busy={busy} disabled={!ready || busy} onClick={save}>{editing ? 'Save' : 'Make it'}</FootButton>}
                </SheetFoot>
            </div>
        </Modal>
    );
}

/**
 * A flow, made in two steps (a guided making sheet): its name, then its
 * automations in the order they run. Saving writes the flow (POST / PUT) and
 * then chains it: each step after the first is set to start when the one
 * before it finishes (data/lists.js chainPlan), one PUT a step, in order.
 * A step whose trigger that replaces is said before saving; a tool's own
 * schedule can only be the first step. If a PUT fails, the sheet says which
 * steps were chained and which were not, and stays open to try again.
 * `automations`: the automations it may hold (automationItem).
 */
export function FlowSheet({ groupId, flow = null, automations, onClose, onSaved }) {
    const [name, setName] = useState(flow?.name || '');
    const [detail, setDetail] = useState(flow?.detail || '');
    const [steps, setSteps] = useState(flow?.steps || []);
    const [at, setAt] = useState(flow ? 'steps' : 'name');
    // a move that was refused, said on its step: { id, text }
    const [refused, setRefused] = useState(null);
    // the flow once written (a retry PUTs it rather than making a second)
    const [written, setWritten] = useState(flow);
    // what the last save did, per step: { chained: [id], failed: { id: words } }
    const [outcome, setOutcome] = useState(null);
    // schemas by id, so a step that starts on a change names what changes
    const [schemas, setSchemas] = useState([]);
    useEffect(() => {
        let live = true;
        Promise.resolve(api.getSchemas?.({ limit: 200, ...(groupId ? { group_id: groupId } : {}) })).then((x) => { if (live) setSchemas(x || []); }).catch(() => {});
        return () => { live = false; };
    }, [groupId]);
    const schemaOf = (id) => schemaWord(schemas.find((x) => String(x.id) === String(id)));
    const byId = (id) => automations.find((a) => String(a.id) === String(id)) || null;
    const nameOf = (id) => byId(id)?.name || 'An automation';
    const plan = chainPlan(steps, byId, schemaOf);
    const changes = plan.filter((p) => p.change);
    const blocked = plan.filter((p) => p.blocked);

    const edit = (next) => { setRefused(null); setOutcome(null); setSteps(next); };
    const move = (i, j) => {
        const next = [...steps];
        [next[i], next[j]] = [next[j], next[i]];
        // a tool's own schedule may only start the flow
        const bad = next.map((id, k) => ({ id, why: byId(id) ? cannotStandAt(byId(id), k) : null })).find((x) => x.why);
        if (bad) { setRefused({ id: bad.id, text: bad.why }); return; }
        edit(next);
    };
    // only the first step may be a tool's own schedule
    const left = automations.filter((a) => !steps.includes(a.id) && (steps.length === 0 || !isToolSchedule(a)));

    const save = async () => {
        let saved = written;
        try {
            saved = await autosApi.saveFlow(groupId, written, { name: name.trim(), description: detail || null, automation_ids: steps });
        } catch (e) {
            throw new Error(automationError(e, 'The flow could not be saved.'));
        }
        if (!written && saved?.id) setWritten({ id: saved.id });
        const chained = [];
        const failed = {};
        for (const p of plan) {
            if (!p.change) continue;
            try {
                // one at a time, in order: each names the step before it
                // eslint-disable-next-line no-await-in-loop
                await autosApi.update(groupId, p.id, chainBody(byId(p.id), p.prevId));
                chained.push(p.id);
            } catch (e) {
                failed[p.id] = automationError(e, 'It could not be changed.');
            }
        }
        setOutcome({ chained, failed });
        const missed = Object.keys(failed);
        if (missed.length) {
            onSaved?.({ stay: true });
            const done = chained.length ? `Chained: ${listWords(chained.map(nameOf))}.` : 'No step was chained.';
            throw new Error(`The flow is saved. ${done} Not chained: ${listWords(missed.map(nameOf))}. Try again to chain them.`);
        }
        onSaved?.();
    };

    const named = !!name.trim();
    const nameStep = {
        id: 'name',
        title: 'Name',
        say: named ? name.trim() : undefined,
        state: named ? 'done' : 'left',
        instruction: 'Name the flow, so you know it in a list.',
        content: (
            <div className="plugins-form">
                <Field id="plugins-flow-name" label="Name">
                    <input id="plugins-flow-name" className="plugins-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                </Field>
                <Field id="plugins-flow-detail" label="What it does (optional)">
                    <input id="plugins-flow-detail" className="plugins-input" value={detail} onChange={(e) => setDetail(e.target.value)} />
                </Field>
            </div>
        ),
    };
    const actLabel = changes.length ? 'Save and chain them' : flow ? 'Save' : 'Make the flow';
    let hint;
    if (!named) hint = 'Name it first.';
    else if (!steps.length) hint = 'Add a step first.';
    else if (blocked.length) hint = blocked[0].blocked;
    const stepsStep = {
        id: 'steps',
        title: 'Steps',
        say: steps.length ? `${steps.length} ${steps.length === 1 ? 'step' : 'steps'}` : undefined,
        state: steps.length ? 'done' : 'left',
        instruction: 'Each step starts when the one before it finishes.',
        content: (
            <div className="plugins-form">
                {steps.length > 0 && (
                    <ol className="plugins-flowsteps" aria-label="Steps, in order">
                        {plan.map((p, i) => {
                            const a = byId(p.id);
                            const n = p.name;
                            const off = a && standingOf(a).state === 'off';
                            const said = refused?.id === p.id ? refused.text : p.blocked;
                            const failed = outcome?.failed?.[p.id];
                            const wasChained = outcome?.chained?.includes(p.id);
                            return (
                                <li key={p.id} className={`plugins-flowstep${said || failed ? ' is-held' : ''}`}>
                                    <span className="plugins-flowstep__n" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                                    <span className="plugins-flowstep__name">
                                        {n}
                                        {a && <small>{[a.plugin, capital(startsPhrase(a, (id) => byId(id)?.name, schemaOf)), off && 'Off'].filter(Boolean).join(' · ')}</small>}
                                        {said && <small className="plugins-flowstep__why" role="alert">{said}</small>}
                                        {failed && <small className="plugins-flowstep__why" role="alert">{`Not chained: ${failed}`}</small>}
                                        {wasChained && !failed && <small className="plugins-flowstep__ok">{p.prevName ? `Chained to ${p.prevName}` : 'Now runs when you run it'}</small>}
                                    </span>
                                    <button type="button" className="plugins-flowstep__act" aria-label={`Move ${n} up`} title={`Move ${n} up`} disabled={i === 0} onClick={() => move(i - 1, i)}><ArrowUp size={13} /></button>
                                    <button type="button" className="plugins-flowstep__act" aria-label={`Move ${n} down`} title={`Move ${n} down`} disabled={i === steps.length - 1} onClick={() => move(i, i + 1)}><ArrowDown size={13} /></button>
                                    <button type="button" className="plugins-flowstep__act" aria-label={`Take ${n} out`} title={`Take ${n} out`} onClick={() => edit(steps.filter((x) => x !== p.id))}><X size={13} /></button>
                                </li>
                            );
                        })}
                    </ol>
                )}
                <Field id="plugins-flow-add" label={steps.length ? 'Add the next step' : 'Add the first step'}>
                    <Picker id="plugins-flow-add" fill label={steps.length ? 'Add the next step' : 'Add the first step'} value="" disabled={!left.length}
                        placeholder={left.length ? 'Choose an automation' : 'Nothing else can be added'}
                        onChange={(id) => edit([...steps, id])}
                        options={left.map((a) => ({ value: a.id, label: a.name, hint: a.plugin || undefined }))} />
                </Field>
                {changes.length > 0 && (
                    <div className="plugins-flowchanges" aria-live="polite">
                        <span className="plugins-label">When you save</span>
                        <ul>{changes.map((p) => <li key={p.id}>{p.line}</li>)}</ul>
                    </div>
                )}
            </div>
        ),
        act: { label: actLabel, run: save, disabled: !named || !steps.length || blocked.length > 0, hint },
    };
    return (
        <StepSheet
            label={flow ? `Edit ${flow.name}` : 'New flow'}
            head={flow ? 'Changing a flow' : 'Making a flow'}
            title={flow ? `Edit ${flow.name}` : 'New flow'}
            steps={[nameStep, stepsStep]}
            at={at}
            onAt={setAt}
            onClose={onClose}
        />
    );
}

/** Raw details, to read: a few labelled JSON blocks. */
export function ReadSheet({ title, parts, onClose }) {
    const list = (parts || []).filter((p) => p && p.value != null);
    return (
        <Modal open onClose={onClose} title={title} size="tall">
            <div className="plugins-read">
                {list.map((p) => (
                    <section key={p.label} className="plugins-read__part">
                        <span className="plugins-label">{p.label}</span>
                        <pre className="plugins-json">{JSON.stringify(p.value, null, 2)}</pre>
                    </section>
                ))}
            </div>
        </Modal>
    );
}
